/**
 * On-demand loader for the remaining third-party widget scripts.
 *
 * These used to be three unconditional `<Script strategy="idle">` tags in
 * gatsby-browser.js, so every page paid for Trustpilot + Typeform (+ HubSpot,
 * now removed) even though only a handful of pages render a widget. The
 * Lighthouse trace attributed ~90-100 ms of main-thread work per script to
 * pages that had nothing to show for it, which lands straight on INP.
 *
 * Instead of importing this from every widget component (and risking a widget
 * that renders with no script), the loader looks for the widget markup in the
 * DOM. Both widgets are server-rendered — Trustpilot ships a
 * `.trustpilot-widget` div, Typeform a `data-tf-*` attribute — and Typeform
 * embeds in particular arrive as raw HTML from Strapi `basic.html` blocks,
 * which no component file could ever declare up front. A DOM scan at idle
 * covers both cases and keeps the decision in one place.
 *
 * Call `loadThirdPartyScriptsForDocument()` on first paint and on every route
 * change (see gatsby-browser.js). Components may also call
 * `loadThirdPartyScript("trustpilot")` directly if they mount a widget after
 * hydration.
 */

export type ThirdPartyScriptId = "trustpilot" | "typeform";

type ScriptDefinition = {
  /** Script URL. */
  src: string;
  /** Selector that proves the page actually renders this widget. */
  selector: string;
};

const SCRIPTS: Record<ThirdPartyScriptId, ScriptDefinition> = {
  trustpilot: {
    src: "https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js",
    selector: ".trustpilot-widget",
  },
  typeform: {
    // Typeform has no component of its own since the CRM forms migration
    // (2026-08); embeds only reach the page through CMS-authored HTML.
    src: "https://embed.typeform.com/next/embed.js",
    selector:
      "[data-tf-live],[data-tf-widget],[data-tf-popup],[data-tf-slider],[data-tf-sidetab],[data-tf-popover]",
  },
};

const requested = new Set<ThirdPartyScriptId>();

const whenIdle = (callback: () => void): void => {
  if (typeof window === "undefined") return;
  const idle = (window as any).requestIdleCallback;
  if (typeof idle === "function") {
    idle(callback, { timeout: 3000 });
  } else {
    window.setTimeout(callback, 1);
  }
};

/**
 * Inject a third-party script once per session. Safe to call repeatedly and
 * safe to call during SSR (it is a no-op there).
 */
export const loadThirdPartyScript = (id: ThirdPartyScriptId): void => {
  if (typeof document === "undefined") return;
  if (requested.has(id)) return;
  requested.add(id);

  const script = document.createElement("script");
  script.src = SCRIPTS[id].src;
  script.async = true;
  script.dataset.thirdParty = id;
  document.head.appendChild(script);
};

/**
 * Trustpilot's bootstrap only scans the DOM once, when it loads. A client-side
 * navigation that brings in a new widget after that has to be wired manually,
 * which is also what the old always-on `<Script>` never did.
 */
const refreshTrustpilotWidgets = (): void => {
  const trustpilot = (window as any).Trustpilot;
  if (!trustpilot || typeof trustpilot.loadFromElement !== "function") return;
  document
    .querySelectorAll(SCRIPTS.trustpilot.selector)
    .forEach((element) => trustpilot.loadFromElement(element, true));
};

/**
 * Scan the current document at idle and load only the scripts whose widgets
 * are actually present.
 */
export const loadThirdPartyScriptsForDocument = (): void => {
  if (typeof document === "undefined") return;
  whenIdle(() => {
    (Object.keys(SCRIPTS) as ThirdPartyScriptId[]).forEach((id) => {
      if (!document.querySelector(SCRIPTS[id].selector)) return;
      if (requested.has(id)) {
        if (id === "trustpilot") refreshTrustpilotWidgets();
        return;
      }
      loadThirdPartyScript(id);
    });
  });
};
